import type { IActionDefinitionDto } from "@kuika/kuika-cl-model";

/**
 * KOA-18179 — hide platform-infrastructure action families from the LLM's view
 * of the project action catalog.
 *
 * Why: the catalog handed to the Designer (`ui_project.query_datasource_actions`)
 * carries `id`, `name`, `returnFields`, `parameters`, `returnType` and **no
 * description of any kind** — measured across all 500 entries of a live 536-action
 * project. Neither does `simplified-actions.json` (255 entries: Id, Name,
 * ActionCategory, Parameters), and the curated planner menu is "names only" by
 * design. The vector index has a `description` slot but falls back to the
 * HUMANIZED NAME when an action ships without one. So for a platform action the
 * model's entire evidence is its own name spelled with spaces.
 *
 * Live failure (session cfd45257-4043-4802-9641-4b423fdb8992, request
 * chat_1786539957283_m1rhnj): building a risk-management app, the Designer needed
 * a "Son işlem izleri" panel and wrote `<Table datasource="GetAuditLogs">`.
 * `GetAuditLogs` is real — but it is Kuika's OWN audit telemetry (`AuditLogDto`:
 * CreatedAt, CreatedUsername, TargetType, DurationMs, Outcome, RowsAffected…),
 * not application data, and it has no `Id`. A row NAVIGATE then carried
 * `screenInputs: [{ id: "current.Id" }]`, which resolved against that screen-level
 * read and produced `KV1008 — Target field 'Id' of action 'GetAuditLogs' no longer
 * exists`. One Error-severity validation entry aborts codegen, so Preview never
 * opened. The same class had already been seen with `GetCategories`.
 *
 * The model could not have known better: nothing on any surface says "this is
 * platform infrastructure, not your app's data". Until those descriptions exist,
 * the honest fix is to stop offering these families at all.
 *
 * **Scope: LLM surfaces only.** This is deliberately NOT folded into
 * `action-definition-filter.ts`, which also feeds the human designer's action
 * picker — a developer must still be able to pick `GetAuditLogs` by hand. Only
 * the `mcp-tools/handlers/*` catalog reads apply this.
 *
 * **Temporary.** These families come back once the actions carry real
 * descriptions (or a platform/app flag) that let the model choose correctly.
 * Re-enabling is one edit: drop a family from {@link HIDDEN_ACTION_FAMILIES}.
 */

/** Families currently withheld from the LLM. Remove an entry to re-enable it. */
export const HIDDEN_ACTION_FAMILIES = ["audit", "category", "document"] as const;

export type HiddenActionFamily = (typeof HIDDEN_ACTION_FAMILIES)[number];

/**
 * Vendor-prefixed integrations are their own concern and stay VISIBLE — their
 * names carry the vendor, so they cannot be mistaken for app data the way a bare
 * `GetAuditLogs` can. The guard exists because the document-family patterns below
 * would otherwise swallow `IyzicoInitializeCheckoutForm` (matches "CheckOut"),
 * `DropboxCreateFolder` and `Office365ListMailFolders` (match "Folder").
 */
const VENDOR_PREFIXED =
  /^(Trello|Jira|Slack|GoogleTask|Google|Office365|Iyzico|Param|Dropbox|OneDrive|Whatsapp|Sap|Stripe|Netgsm|Twilio|Sendgrid)/i;

/**
 * Platform return-type names per family. The return type is the ONLY reliable
 * discriminator in the payload: `AuditLogDto`, `KCategoryModel`, `KDocument*`
 * are platform DTOs no project action can accidentally share. Name regexes alone
 * cannot carry this decision — the repo's own `looksLikeReadAction` only matches
 * underscore-segmented names and answers false for `GetAuditLogs` alike.
 */
const FAMILY_RETURN_TYPES: Readonly<Record<HiddenActionFamily, readonly string[]>> = {
  audit: ["AuditLogDto", "AuditLogStatsResult", "KDocumentAuditLog", "PurgeResult"],
  category: ["KCategoryModel"],
  document: [
    "KDocument",
    "KDocumentVersion",
    "KDocumentPermission",
    "KDocumentShare",
    "CommitResult",
    "CheckOutResult",
    "CheckOutStatusResult",
    "CheckedOutDocItem",
    "CheckoutFormResource",
    "MyCheckedOutItem",
    "DraftDocItem",
    "DocTypeItem",
    "DocumentActivitySummaryResult",
    "DocumentByTagItem",
    "DocumentCommentItem",
    "DocumentInfo",
    "DocumentMetadataResult",
    "DocumentParameterContextResult",
    "DocumentReference",
    "DocumentReferenceResult",
    "DocumentStatusSummaryResult",
    "DocumentTagItem",
    "DocumentThumbnailResult",
    "DocumentTreeNode",
    "DocumentUploadResult",
    "RelatedDocumentItem",
    "EmptyFolderItem",
    "ExpiringShareItem",
    "FolderByTagItem",
    "FolderDetailResult",
    "FolderPathItem",
    "FolderStatsResult",
    "FolderTagCloudItem",
    "FolderTagItem",
    "LargestDocItem",
    "OldDocItem",
    "ShareLinkResult",
    "UploadLinkResult",
    "VersionBase64Result",
    "VersionDetailResult",
    "VersionStatisticsResult",
    "IndexStats",
    "IndexingStatusResult"
  ]
};

/**
 * Name patterns for family members the payload gives no return type for (~half
 * the catalog ships `returnType` undefined — `ExportAuditLogsCsv`,
 * `CreateCategory`, most document mutations). Anchored to whole subject words so
 * a project's own `RiskCategoryScore` or `DocumentedControl` is not swallowed.
 */
const FAMILY_NAME_PATTERNS: Readonly<Record<HiddenActionFamily, RegExp>> = {
  audit: /AuditLog/i,
  category: /^(?:Create|Update|Delete|Get)(?:Template)?Categor(?:y|ies)$/,
  document:
    /(?:Document|Folder|Draft|CheckOut|UndoCheckOut|Version|Thumbnail|ShareLink|SearchIndex|SearchRecord|ReindexDocument|IndexUnindexed)/i
};

/** The family an action belongs to, or null when it is ordinary app data. */
export function hiddenFamilyOf(
  action: Pick<IActionDefinitionDto, "name" | "returnTypeModel">
): HiddenActionFamily | null {
  const name: string = action.name ?? "";
  if (name.length === 0) return null;
  if (VENDOR_PREFIXED.test(name)) return null;
  const returnTypeName: string = action.returnTypeModel?.name ?? "";
  for (const family of HIDDEN_ACTION_FAMILIES) {
    if (returnTypeName.length > 0 && FAMILY_RETURN_TYPES[family].includes(returnTypeName)) return family;
    if (FAMILY_NAME_PATTERNS[family].test(name)) return family;
  }
  return null;
}

/** True when this action is withheld from the LLM's catalog view. */
export function isHiddenPlatformAction(action: Pick<IActionDefinitionDto, "name" | "returnTypeModel">): boolean {
  return hiddenFamilyOf(action) !== null;
}

/**
 * Drops the withheld families. Applied ONLY on `mcp-tools/handlers/*` catalog
 * reads — never on the human designer's action picker.
 */
export function hidePlatformActionsFromLlm(actions: readonly IActionDefinitionDto[]): IActionDefinitionDto[] {
  return actions.filter((action: IActionDefinitionDto): boolean => !isHiddenPlatformAction(action));
}
